import type { WizardFlowDefinition } from '../flowTypes';
import { DL_HEAVY_CATEGORIES, DL_LIGHT_CATEGORIES } from '../../constants/driver';
import type { SelfServiceData } from '../../types';
import DriverStartStep from '../../components/driver/DriverStartStep';
import DriverCaseTypeStep from '../../components/driver/DriverCaseTypeStep';
import DriverCategoryStep from '../../components/driver/DriverCategoryStep';
import DriverParentConsentStep from '../../components/driver/DriverParentConsentStep';
import DriverParentSignatureStep from '../../components/driver/DriverParentSignatureStep';
import DriverMedicalStep from '../../components/driver/DriverMedicalStep';
import DriverVisionStep from '../../components/driver/DriverVisionStep';
import DriverSummaryStep from '../../components/driver/DriverSummaryStep';
import DriverPaymentStep from '../../components/driver/DriverPaymentStep';
import DriverConfirmationStep from '../../components/driver/DriverConfirmationStep';
import PhotoMethodStep from '../../components/steps/PhotoMethodStep';
import PhotoInfoStep from '../../components/steps/PhotoInfoStep';
import ContactInfoStep from '../../components/steps/ContactInfoStep';
import TimeSelectionStep from '../../components/steps/TimeSelectionStep';
import SignStep from '../../components/sign/SignStep';
import GdprStep from '../../components/shared/GdprStep';
import { requireContactInfo, requireTimeSelection } from '../sharedValidators';

const needsMedical = (data: SelfServiceData): boolean => !!(data.dlCategory && DL_HEAVY_CATEGORIES.has(data.dlCategory));

const needsVision = (data: SelfServiceData): boolean => {
  const lightCategories = new Set(['AM', 'A', 'B']);
  return data.dlCaseType === 'first_time' && !!(data.dlCategory && lightCategories.has(data.dlCategory));
};

const shouldAskParentConsent = (data: SelfServiceData): boolean => data.dlCaseType === 'first_time';
const requiresParentSignature = (data: SelfServiceData): boolean => data.dlParentConsentRequested === true;

const driverPhases = [
  { id: 'intro', name: 'Start' },
  { id: 'requirements', name: 'Forudsætninger' },
  { id: 'details', name: 'Oplysninger' },
  { id: 'review', name: 'Gennemse' },
  { id: 'completion', name: 'Afslut' }
] as const;

const driverFlow: WizardFlowDefinition = {
  id: 'koerekort',
  startStep: 'dl_start',
  phases: driverPhases,
  steps: {
    dl_start: { id: 'dl_start', name: 'Kørekort', component: DriverStartStep, next: 'gdpr', phase: 'intro' },
    gdpr: { id: 'gdpr', name: 'GDPR', component: GdprStep, next: 'dl_case_type', phase: 'intro' },
    dl_case_type: {
      id: 'dl_case_type',
      name: 'Type',
      component: DriverCaseTypeStep,
      getNext: (data) => {
        if (data.dlCaseType === 'first_time' || data.dlCaseType === 'upgrade') {return 'dl_category';}
        if (needsMedical(data)) {return 'dl_medical';}
        if (needsVision(data)) {return 'dl_vision';}
        return 'photo_method';
      },
      phase: 'requirements'
    },
    dl_category: {
      id: 'dl_category',
      name: 'Kategori',
      component: DriverCategoryStep,
      getNext: (data) => {
        if (shouldAskParentConsent(data)) {return 'dl_parent_consent';}
        if (needsMedical(data)) {return 'dl_medical';}
        if (needsVision(data)) {return 'dl_vision';}
        return 'photo_method';
      },
      phase: 'requirements'
    },
    dl_parent_consent: {
      id: 'dl_parent_consent',
      name: 'Forældres samtykke',
      component: DriverParentConsentStep,
      getNext: (data) => {
        if (needsMedical(data)) {return 'dl_medical';}
        if (needsVision(data)) {return 'dl_vision';}
        return 'photo_method';
      },
      phase: 'requirements'
    },
    dl_medical: {
      id: 'dl_medical',
      name: 'Lægeattest',
      component: DriverMedicalStep,
      getNext: (data) => (needsVision(data) ? 'dl_vision' : 'photo_method'),
      phase: 'requirements'
    },
    dl_vision: { id: 'dl_vision', name: 'Synstest', component: DriverVisionStep, next: 'photo_method', phase: 'requirements' },
    photo_method: {
      id: 'photo_method',
      name: 'Foto metode',
      component: PhotoMethodStep,
      next: 'photo_info',
      validate: (data) => (data.photoMethod ? null : 'Vælg venligst, hvor du får taget foto.'),
      phase: 'details'
    },
    photo_info: { id: 'photo_info', name: 'Pasfoto', component: PhotoInfoStep, next: 'contact_info', phase: 'details' },
    contact_info: {
      id: 'contact_info',
      name: 'Kontakt',
      component: ContactInfoStep,
      next: 'select_time',
      validate: requireContactInfo,
      phase: 'details'
    },
    select_time: {
      id: 'select_time',
      name: 'Vælg tid',
      component: TimeSelectionStep,
      next: 'dl_summary',
      validate: requireTimeSelection,
      phase: 'details'
    },
    dl_summary: {
      id: 'dl_summary',
      name: 'Oversigt',
      component: DriverSummaryStep,
      getNext: (data) => (requiresParentSignature(data) ? 'dl_parent_signature' : 'sign'),
      phase: 'review'
    },
    dl_parent_signature: {
      id: 'dl_parent_signature',
      name: 'Forældrenes underskrift',
      component: DriverParentSignatureStep,
      next: 'sign',
      phase: 'completion'
    },
    sign: { id: 'sign', name: 'Signer', component: SignStep, next: 'dl_payment', phase: 'completion' },
    dl_payment: { id: 'dl_payment', name: 'Betaling', component: DriverPaymentStep, next: 'dl_confirmation', phase: 'completion' },
    dl_confirmation: { id: 'dl_confirmation', name: 'Bekræftelse', component: DriverConfirmationStep, phase: 'completion' }
  }
};

export { driverFlow };
